// src/lib/translation-server.ts
import { createServerFn } from "@tanstack/react-start";
import {
  getCookie,
  setCookie,
  getRequest,
  getRequestHeaders,
  setResponseHeader,
} from "@tanstack/react-start/server";
import z from "zod";
import { supportedLanguages } from "./init";
import { pickAcceptLanguage } from "./accept-language";

/**
 * Headers that keep a shared cache (Vercel edge, Cloudflare, an ISP/corporate
 * proxy) from storing one visitor's language-specific response and replaying it
 * to everyone. The locale is resolved per-request from `Accept-Language` + the
 * `locale` cookie, so any of these responses is user-specific. Pass this to the
 * `redirect()` calls that pick a locale so the 307 is never shared-cached.
 */
export const LANGUAGE_RESPONSE_CACHE_HEADERS: Record<string, string> = {
  "Cache-Control": "private, no-store",
  Vary: "Accept-Language, Cookie",
};

/**
 * Marks the current response as language-specific and uncacheable by shared
 * caches. Used for the render (200) path where there is no `redirect()` to
 * attach headers to. Only sets Cache-Control: the render response's `Vary` is
 * later overwritten by the compression layer, and `no-store` already prevents
 * shared caching on its own.
 */
function markLanguageResponseUncacheable(): void {
  setResponseHeader("Cache-Control", "private, no-store");
}

type TranslationParams = {
  lng: string;
  ns: string;
};

/**
 * Detects the browser's preferred language from the Accept-Language header
 * and maps it to one of the supported languages
 */
function detectBrowserLanguage(): string {
  try {
    const request = getRequest();
    return pickAcceptLanguage(
      request.headers.get("Accept-Language"),
      supportedLanguages
    );
  } catch (error) {
    console.warn("Failed to detect browser language:", error);
    return "en"; // Default fallback on error
  }
}

export const getLocaleCookie = createServerFn({ method: "GET" }).handler(
  async () => {
    return getCookie("locale");
  }
);

/**
 * Gets the preferred locale: cookie if exists, otherwise browser language, otherwise default
 */
export const getPreferredLocale = createServerFn({ method: "GET" }).handler(
  async () => {
    // The locale we return is derived from the `locale` cookie and the browser's
    // Accept-Language header, so the response that acts on it is user-specific.
    // Mark it uncacheable here (covers the render path); redirects additionally
    // pass LANGUAGE_RESPONSE_CACHE_HEADERS. Without this a shared cache pins the
    // whole site to the first cached visitor's language (usually a bot → "en").
    markLanguageResponseUncacheable();

    // First check for locale cookie
    const cookieLocale = getCookie("locale");
    if (cookieLocale && supportedLanguages.includes(cookieLocale)) {
      return cookieLocale;
    }

    // If no valid cookie, detect from browser Accept-Language header
    const browserLocale = detectBrowserLanguage();
    return browserLocale;
  }
);

export const getServerHeaders = createServerFn({ method: "GET" }).handler(
  async () => {
    const headers = getRequestHeaders();
    // Convert Headers object to plain object to ensure serializability
    // Headers objects can contain functions and aren't serializable
    if (headers instanceof Headers) {
      const plainHeaders: Record<string, string> = {};
      headers.forEach((value, key) => {
        // Only include string values to ensure serializability
        if (typeof value === "string") {
          plainHeaders[key] = value;
        }
      });
      return plainHeaders;
    }
    // If it's already a plain object, filter out any non-serializable values
    const plainHeaders: Record<string, string> = {};
    for (const [key, value] of Object.entries(headers)) {
      // Only include string values to ensure serializability
      if (typeof value === "string") {
        plainHeaders[key] = value;
      }
    }
    return plainHeaders;
  }
);

export const changeLanguage = createServerFn({
  method: "POST",
})
  .inputValidator(z.string())
  .handler(async ({ data: lng }) => {
    setCookie("locale", lng, {
      path: "/",
      maxAge: 60 * 60 * 24 * 365, //1 year
    });
    return lng;
  });

export const loadTranslation = createServerFn({
  method: "GET",
})
  .inputValidator((data: TranslationParams) => {
    // Validate the language and namespace parameters
    if (!data.lng || !data.ns) {
      throw new Error("Language and namespace are required");
    }
    return data;
  })
  .handler(async ({ data: { lng, ns } }) => {
    try {
      // Use dynamic import to load translation files from src/locales
      // This works better in serverless environments like Vercel
      const translationModule = await import(`../../locales/${lng}/${ns}.json`);
      return translationModule.default || translationModule;
    } catch (error) {
      // If file doesn't exist or can't be read, return empty object
      console.warn(`Translation file not found: ${lng}/${ns}.json`);
      return {};
    }
  });

// Optional: Load multiple translations at once for better performance
export const loadMultipleTranslations = createServerFn({
  method: "GET",
})
  .inputValidator((data: { languages: string[]; namespaces: string[] }) => {
    if (!Array.isArray(data.languages) || !Array.isArray(data.namespaces)) {
      throw new Error("Languages and namespaces must be arrays");
    }
    return data;
  })
  .handler(async ({ data: { languages, namespaces } }) => {
    const result: Record<string, Record<string, any>> = {};

    for (const lng of languages) {
      result[lng] = {};
      for (const ns of namespaces) {
        try {
          // Use dynamic import to load translation files from src/locales
          const translationModule = await import(
            `../../locales/${lng}/${ns}.json`
          );
          result[lng][ns] = translationModule.default || translationModule;
        } catch (error) {
          result[lng][ns] = {};
        }
      }
    }

    return result;
  });
